import "leaflet/dist/leaflet.css";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";

const API = process.env.REACT_APP_API;
const zipGeoURL = "/geo/tl_2020_us_zcta520.geojson";

const STATE_CENTERS = {
  AL: [32.36, -86.79], AK: [61.22, -152.40], AZ: [33.77, -111.43],
  AR: [34.95, -92.37], CA: [36.12, -119.85], CO: [39.06, -105.31],
  CT: [41.60, -72.73], DE: [39.32, -75.53], FL: [27.77, -81.66],
  GA: [33.75, -83.64], HI: [21.09, -157.50], ID: [44.26, -114.74],
  IL: [40.34, -89.40], IN: [39.77, -86.16], IA: [42.01, -93.77],
  KS: [38.53, -96.73], KY: [37.67, -84.67], LA: [31.17, -91.83],
  ME: [44.69, -69.40], MD: [39.06, -76.79], MA: [42.24, -71.54],
  MI: [43.33, -84.55], MN: [45.73, -95.57], MS: [32.77, -89.67],
  MO: [38.46, -92.33], MT: [47.05, -110.36], NE: [41.49, -99.90],
  NV: [38.31, -117.06], NH: [43.45, -71.55], NJ: [40.06, -74.74],
  NM: [34.84, -106.25], NY: [42.95, -74.22], NC: [35.76, -79.02],
  ND: [47.40, -99.78], OH: [40.39, -82.76], OK: [35.47, -96.93],
  OR: [44.98, -122.07], PA: [40.27, -77.19], RI: [41.68, -71.51],
  SC: [33.84, -80.93], SD: [44.30, -99.90], TN: [35.75, -86.68],
  TX: [31.05, -97.56], UT: [40.11, -111.89], VT: [44.05, -72.71],
  VA: [37.77, -78.17], WA: [47.39, -121.19], WV: [38.47, -80.97],
  WI: [44.27, -89.62], WY: [42.75, -107.30],
};

const US_STATES = Object.keys(STATE_CENTERS).sort();

const extractCoordinates = (coordinates) => {
  if (!Array.isArray(coordinates)) {
    return [];
  }
  if (
    coordinates.length >= 2 &&
    typeof coordinates[0] === "number" &&
    typeof coordinates[1] === "number"
  ) {
    return [coordinates];
  }
  return coordinates.flatMap(extractCoordinates);
};

function MapUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

export default function PracticeArea() {
  const [zipGeoData, setZipGeoData] = useState(null);
  const [selectedZips, setSelectedZips] = useState([]);
  const [name, setName] = useState("My Practice Area");
  const [savedAreas, setSavedAreas] = useState([]);
  const [activeAreaId, setActiveAreaId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mapState, setMapState] = useState("NY");
  const [mapCenter, setMapCenter] = useState(STATE_CENTERS.NY);
  const [mapZoom, setMapZoom] = useState(7);
  const [message, setMessage] = useState("");
  const [areasLoading, setAreasLoading] = useState(false);
  const [geoLoadError, setGeoLoadError] = useState("");
  const geoJsonRef = useRef(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // Load geo data
  useEffect(() => {
    let cancelled = false;

    const loadGeoData = async () => {
      try {
        const res = await fetch(zipGeoURL);
        if (!res.ok) {
          throw new Error("Failed to load ZIP map data");
        }

        const data = await res.json();
        if (!cancelled) {
          setZipGeoData(data);
          setGeoLoadError("");
        }
      } catch (e) {
        console.error("Failed to load ZIP geo data:", e);
        if (!cancelled) {
          setGeoLoadError("Failed to load ZIP map data.");
        }
      }
    };

    loadGeoData();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load saved areas
  const loadAreas = useCallback(async () => {
    setAreasLoading(true);
    try {
      const res = await fetch(`${API}/practice-areas`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to load practice areas");
      }
      setSavedAreas(await res.json());
    } catch (e) {
      console.error("Failed to load practice areas:", e);
      setMessage(`Error: ${e.message || "Failed to load practice areas"}`);
    } finally {
      setAreasLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadAreas(); }, [loadAreas]);

  // Update map center when state changes
  useEffect(() => {
    if (mapState && STATE_CENTERS[mapState]) {
      setMapCenter(STATE_CENTERS[mapState]);
      setMapZoom(7);
    }
  }, [mapState]);

  const lastZipCountRef = React.useRef(0);
  useEffect(() => {
    if (!zipGeoData) {
      return;
    }

    // Only refocus on BULK changes (loading a saved area), never on
    // single-zip clicks — the user is already looking where they clicked.
    const prevCount = lastZipCountRef.current;
    lastZipCountRef.current = selectedZips.length;
    if (Math.abs(selectedZips.length - prevCount) <= 2 && prevCount !== 0) {
      return;
    }

    if (selectedZips.length === 0) {
      if (mapState && STATE_CENTERS[mapState]) {
        setMapCenter(STATE_CENTERS[mapState]);
        setMapZoom(7);
      }
      return;
    }

    const zipSet = new Set(selectedZips);
    const focusFeatures = (zipGeoData.features || []).filter((feature) =>
      zipSet.has(feature.properties.ZCTA5CE20)
    );
    const coords = focusFeatures.flatMap((feature) =>
      extractCoordinates(feature.geometry?.coordinates)
    );

    if (coords.length > 0) {
      const avgLon = coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length;
      const avgLat = coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length;
      setMapCenter([avgLat, avgLon]);
      if (focusFeatures.length <= 10) {
        setMapZoom(9);
      } else if (focusFeatures.length <= 40) {
        setMapZoom(8);
      } else if (focusFeatures.length <= 100) {
        setMapZoom(7);
      } else {
        setMapZoom(6);
      }
    }
  }, [mapState, selectedZips, zipGeoData]);

  const toggleZip = useCallback((zip) => {
    setSelectedZips((prev) =>
      prev.includes(zip) ? prev.filter((z) => z !== zip) : [...prev, zip]
    );
  }, []);

  const removeZip = useCallback((zip) => {
    setSelectedZips((prev) => prev.filter((z) => z !== zip));
  }, []);

  const loadArea = useCallback((area) => {
    setActiveAreaId(area.id);
    setName(area.name);
    setSelectedZips(area.zip_codes || []);
    setMessage("");
  }, []);

  const handleNew = useCallback(() => {
    setActiveAreaId(null);
    setName("My Practice Area");
    setSelectedZips([]);
    setMessage("");
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      const body = { name, zip_codes: selectedZips };
      let res;
      if (activeAreaId) {
        res = await fetch(`${API}/practice-areas/${activeAreaId}`, {
          method: "PUT", headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API}/practice-areas`, {
          method: "POST", headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      }
      if (res.ok) {
        const saved = await res.json();
        setActiveAreaId(saved.id);
        setMessage("✅ Saved!");
        await loadAreas();
      } else {
        const err = await res.json();
        setMessage(`❌ ${err.error || "Save failed"}`);
      }
    } catch (e) {
      setMessage("❌ Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!activeAreaId) return;
    if (!window.confirm("Delete this practice area?")) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/practice-areas/${activeAreaId}`, {
        method: "DELETE", headers,
      });
      if (res.ok) {
        handleNew();
        await loadAreas();
        setMessage("🗑️ Deleted");
      } else {
        const err = await res.json().catch(() => ({}));
        setMessage(`❌ ${err.error || "Delete failed"}`);
      }
    } catch (e) {
      setMessage("❌ Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const style = useMemo(() => {
    const zipSet = new Set(selectedZips);
    return (feature) => {
      const zip = feature.properties.ZCTA5CE20;
      const isSelected = zipSet.has(zip);
      return {
        fillColor: isSelected ? "#22c55e" : "rgba(200,200,200,0.15)",
        weight: isSelected ? 2 : 0.5,
        color: isSelected ? "#16a34a" : "rgba(255,255,255,0.4)",
        fillOpacity: isSelected ? 0.7 : 1,
      };
    };
  }, [selectedZips]);

  useEffect(() => {
    if (geoJsonRef.current) {
      geoJsonRef.current.setStyle(style);
    }
  }, [style]);

  const onEachFeature = useCallback((feature, layer) => {
    const zip = feature.properties.ZCTA5CE20;
    layer.bindTooltip(`ZIP ${zip}`);
    layer.on("click", () => {
      toggleZip(zip);
    });
  }, [toggleZip]);

  const sortedSelectedZips = [...selectedZips].sort();

  return (
    <div className="flex gap-4" style={{ height: "calc(100vh - 120px)" }}>
      {/* Sidebar */}
      <div className="w-80 flex-shrink-0 bg-white rounded-lg shadow p-4 overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Practice Area Builder</h2>

        {/* Load existing */}
        {savedAreas.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Load Saved</label>
            <select
              className="w-full border rounded px-3 py-2 text-sm"
              value={activeAreaId || ""}
              onChange={(e) => {
                const id = parseInt(e.target.value);
                const area = savedAreas.find((a) => a.id === id);
                if (area) loadArea(area);
              }}
            >
              <option value="">— Select —</option>
              {savedAreas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({(a.zip_codes || []).length} ZIPs)
                </option>
              ))}
            </select>
            <button
              onClick={handleNew}
              className="mt-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded font-medium text-sm"
            >
              + New Practice Area
            </button>
          </div>
        )}
        {areasLoading && (
          <p className="text-xs text-gray-500 mb-4">Loading saved practice areas...</p>
        )}

        {/* Name */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            type="text"
            className="w-full border rounded px-3 py-2"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        {/* State selector */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Navigate to State</label>
          <select
            className="w-full border rounded px-3 py-2 text-sm"
            value={mapState}
            onChange={(e) => setMapState(e.target.value)}
          >
            {US_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={handleSave}
            disabled={saving || selectedZips.length === 0}
            className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-3 py-2 rounded font-medium text-sm"
          >
            {saving ? "Saving..." : activeAreaId ? "Update" : "Save"}
          </button>
          {activeAreaId && (
            <button
              onClick={handleDelete}
              disabled={loading}
              className="bg-white hover:bg-red-50 text-red-600 hover:text-red-700 px-3 py-2 rounded font-medium text-sm border border-red-300"
            >
              Delete
            </button>
          )}
        </div>

        {message && <p className="text-sm mb-3">{message}</p>}

        {/* Selected ZIPs */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Selected ZIPs ({selectedZips.length})
          </h3>
          {selectedZips.length === 0 ? (
            <p className="text-xs text-gray-500">Click ZIP codes on the map to select them</p>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {sortedSelectedZips.map((zip) => (
                <div key={zip} className="flex items-center justify-between bg-green-50 rounded px-2 py-1">
                  <span className="text-sm font-mono">{zip}</span>
                  <button
                    onClick={() => removeZip(zip)}
                    className="bg-white hover:bg-red-50 text-red-500 hover:text-red-700 text-xs font-bold px-2 py-1 rounded border border-red-200"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          {selectedZips.length > 0 && (
            <button
              onClick={() => setSelectedZips([])}
              className="mt-2 bg-white hover:bg-red-50 text-red-500 hover:text-red-700 px-3 py-2 rounded font-medium text-sm border border-red-200"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 rounded-lg overflow-hidden shadow">
        {zipGeoData ? (
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ width: "100%", height: "100%" }}
            scrollWheelZoom={true}
            preferCanvas={true}
          >
            <MapUpdater center={mapCenter} zoom={mapZoom} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              crossOrigin="anonymous"
            />
            <GeoJSON
              ref={geoJsonRef}
              data={zipGeoData}
              style={style}
              onEachFeature={onEachFeature}
            />
          </MapContainer>
        ) : geoLoadError ? (
          <div className="flex items-center justify-center h-full bg-gray-50">
            <p className="text-red-600">{geoLoadError}</p>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-50">
            <p className="text-gray-500">Loading map data...</p>
          </div>
        )}
      </div>
    </div>
  );
}
